import { useEffect, useState } from "react";
import type { TripWithItinerary } from "../../shared/types";
import { api } from "../lib/api";
import { ItineraryView } from "./ItineraryView";
import { Wizard } from "./Wizard";
import { Spinner } from "./ui";

export function TripPage({ slug }: { slug: string }) {
  const [trip, setTrip] = useState<TripWithItinerary | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { trip } = await api.getTrip(slug);
      setTrip(trip);
    } catch (e: any) {
      setErr(e.message || "Could not load trip");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function regenerate() {
    setGenerating(true);
    setErr("");
    try {
      await api.generate(slug);
      await load();
    } catch (e: any) {
      setErr(e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }

  async function finalize() {
    if (!trip?.itinerary) return;
    await api.finalize(slug, trip.itinerary.version);
    await load();
  }

  function share() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) return <div className="mx-auto max-w-4xl"><Spinner label="Loading trip…" /></div>;
  if (err && !trip) return <div className="mx-auto max-w-4xl card text-red-600">{err}</div>;
  if (!trip) return null;

  // Edit mode: reuse the wizard, pre-filled, to change any trip input / traveler profile.
  if (editing) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl font-bold">Edit trip details</h1>
          <span className="text-sm text-slate-400">{trip.destination}</span>
        </div>
        <Wizard
          mode="edit"
          slug={slug}
          initialTrip={trip}
          onComplete={async () => {
            setEditing(false);
            await load();
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  const days =
    trip.startDate && trip.endDate
      ? Math.round((Date.parse(trip.endDate) - Date.parse(trip.startDate)) / 86400000) + 1
      : undefined;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="card bg-gradient-to-br from-brand-600 to-brand-700 text-white">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-100">TravelHub itinerary</p>
            <h1 className="mt-1 text-2xl font-extrabold">{trip.title}</h1>
            <p className="mt-1 text-brand-100">
              {trip.destination}
              {trip.origin ? ` · from ${trip.origin}` : ""}
              {days ? ` · ${days} days` : ""}
              {` · ${trip.travelers.length} traveler${trip.travelers.length > 1 ? "s" : ""}`}
            </p>
            {trip.stops && trip.stops.filter((s) => s.city?.trim()).length > 0 && (
              <p className="mt-1 text-sm font-medium text-white/90">
                🗺️ {trip.stops.filter((s) => s.city?.trim()).map((s) => s.city).join(" → ")}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setEditing(true)} className="btn bg-white/15 text-white ring-1 ring-white/30 hover:bg-white/25">
              ✏️ Edit details
            </button>
            <button onClick={share} className="btn bg-white/15 text-white ring-1 ring-white/30 hover:bg-white/25">
              {copied ? "✓ Link copied" : "🔗 Share"}
            </button>
            {trip.itinerary && (
              <button onClick={finalize} className={`btn ${trip.itinerary.isFinal ? "bg-emerald-400/90 text-emerald-950" : "bg-white/15 text-white ring-1 ring-white/30 hover:bg-white/25"}`}>
                {trip.itinerary.isFinal ? "✓ Finalised" : "📌 Finalise"}
              </button>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {trip.objectives.map((o) => (
            <span key={o} className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs">{o}</span>
          ))}
        </div>
      </div>

      {/* Party summary */}
      <div className="card">
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">The party</h3>
        <div className="flex flex-wrap gap-3">
          {trip.travelers.map((t, i) => (
            <div key={i} className="rounded-xl bg-slate-50 px-3 py-2 text-sm ring-1 ring-slate-100">
              <p className="font-semibold">{t.name} {t.relation && <span className="text-xs font-normal text-slate-400">· {t.relation}</span>}</p>
              {t.interests.length > 0 && <p className="text-xs text-slate-500">{t.interests.join(", ")}</p>}
              {(t.dietary.length > 0 || t.accessibility.length > 0) && (
                <p className="text-xs text-amber-600">{[...t.dietary, ...t.accessibility].join(", ")}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Itinerary */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Itinerary</h2>
        <div className="flex items-center gap-2">
          {trip.itinerary && (
            <span className="text-xs text-slate-400">
              v{trip.itinerary.version} · {trip.itinerary.modelUsed.split(":")[0]}
            </span>
          )}
          <button className="btn-ghost" onClick={regenerate} disabled={generating}>
            {generating ? "Generating…" : trip.itinerary ? "↻ Regenerate" : "✨ Generate"}
          </button>
        </div>
      </div>

      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{err}</p>}

      {generating && !trip.itinerary && (
        <div className="card"><Spinner label="Designing your itinerary…" /></div>
      )}

      {trip.itinerary ? (
        <ItineraryView itinerary={trip.itinerary.content} />
      ) : (
        !generating && (
          <div className="card text-center text-slate-500">
            No itinerary yet. Click <b>Generate</b> to build one from your inputs.
          </div>
        )
      )}
    </div>
  );
}
